import { type OperatorSlug, OTHER_OPERATOR_SLUG, operatorBrand } from '@ev/shared'
import { useId, useMemo } from 'react'
import { useStationIndex } from '../../../data/hooks'
import { useStrings } from '../../../i18n'
import { searchSiteDirectory } from '../../../search/directory'
import { searchOperators } from '../../../search/operators'
import { searchUntrackedOperators, type UntrackedOperator } from '../../../search/untracked'
import { selectFilters, selectMatchingIds, selectQuery, useAppStore } from '../../../state/store'
import { useDebouncedQuery } from '../useDebouncedQuery'

/**
 * Text search over operator, site name and city.
 *
 * Brands come first. "Orlen" typed into a map of Poland means the network, not
 * one of its forecourts, so the box offers the brand above the sites and
 * choosing it toggles the operator filter - the same toggle the checkbox in the
 * panel writes, so the two can never disagree. The text is cleared on the way
 * out, because the filter now carries the intent and leaving the word in the
 * box would narrow the map a second time by name.
 *
 * The site directory comes from the same shared station index the map draws
 * from, so the box never triggers a second download of the national artifact,
 * and the results are restricted to the ids the map published - a result you can
 * click is always a dot you can see.
 *
 * Selecting a site result only writes `selectedSiteId` to the store. Flying the
 * map to it is the map's job, and going through the store keeps that true
 * whether the selection came from this list, from a marker click, or from a
 * pasted link.
 *
 * Below the tracked brands come the networks the registry does not track. They
 * are the inside of the "Inni operatorzy" bucket, read off the artifact rather
 * than the registry, and they exist here because the alternative was answering
 * a search for a real network with nothing: the registry stops at the brands
 * with roughly ten sites or more, and a driver holding a roaming card does not
 * care where that line falls. Choosing one filters for that network alone, not
 * for the whole bucket.
 *
 * Enter takes the first thing on offer, brand before network before site, so the
 * common case - type a brand, press Enter - never needs the mouse. Brands are
 * matched against what is in the input rather than against the debounced store
 * query, because a few hundred names cost nothing to rescan per keystroke and
 * Enter pressed a tenth of a second after the last letter must act on that
 * letter.
 */

/** Untracked networks are the inside of the grey bucket, so they wear its colour. */
const OTHER_OPERATOR_COLOR = operatorBrand(OTHER_OPERATOR_SLUG).color

export const SearchBox = () => {
  const strings = useStrings()
  const inputId = useId()

  const { value, setValue } = useDebouncedQuery()
  const query = useAppStore(selectQuery)
  const matchingIds = useAppStore(selectMatchingIds)
  const filters = useAppStore(selectFilters)
  const setSelectedSiteId = useAppStore((state) => state.setSelectedSiteId)
  const toggleFilter = useAppStore((state) => state.toggleFilter)

  const { resource } = useStationIndex()
  const directory = resource.status === 'ready' ? resource.value.directory : []

  const eligible = useMemo(
    () => (matchingIds === null ? null : new Set(matchingIds)),
    [matchingIds],
  )

  const results = useMemo(
    () => searchSiteDirectory(directory, query, eligible),
    [directory, query, eligible],
  )

  const operators = useMemo(() => searchOperators(value), [value])

  const untracked = useMemo(
    () =>
      searchUntrackedOperators(resource.status === 'ready' ? resource.value.untracked : [], value),
    [resource, value],
  )

  /**
   * The site list follows the debounced query, not the input: it is the map's
   * published match set that decides it, and announcing "nothing found" in the
   * quarter second before the query settles would be a lie about a search that
   * has not run yet. The brand list has no such lag to hide.
   */
  const searching = query.trim() !== ''

  const toggleOperator = (slug: OperatorSlug) => {
    toggleFilter('operators', slug)
    setValue('')
  }

  const toggleUntracked = (operator: UntrackedOperator) => {
    toggleFilter('operatorNames', operator.key)
    setValue('')
  }

  const takeFirstSuggestion = () => {
    const [brand] = operators
    if (brand !== undefined) {
      toggleOperator(brand.slug)
      return
    }
    const [network] = untracked
    if (network !== undefined) {
      toggleUntracked(network)
      return
    }
    const [site] = results
    if (site !== undefined) setSelectedSiteId(site.id)
  }

  return (
    <search className="search">
      <label className="search__label" htmlFor={inputId}>
        {strings.search.label}
      </label>

      <div className="search__field">
        <input
          className="search__input"
          id={inputId}
          type="search"
          autoComplete="off"
          placeholder={strings.search.placeholder}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return
            event.preventDefault()
            takeFirstSuggestion()
          }}
        />

        {value === '' ? null : (
          <button className="ev-button ev-button--quiet" type="button" onClick={() => setValue('')}>
            {strings.search.clear}
          </button>
        )}
      </div>

      {operators.length === 0 ? null : (
        <ul className="search__list search__list--operators" aria-label={strings.search.operators}>
          {operators.map((brand) => {
            const active = filters.operators.includes(brand.slug)
            return (
              <li key={brand.slug}>
                <button
                  className="search__result search__result--operator"
                  type="button"
                  aria-pressed={active}
                  onClick={() => toggleOperator(brand.slug)}
                >
                  <span
                    className="search__result-swatch"
                    style={{ backgroundColor: brand.color }}
                    aria-hidden="true"
                  />
                  <span className="search__result-name">{brand.displayName}</span>
                  <span className="search__result-hint">
                    {active ? strings.search.operatorFilterOn : strings.search.operatorFilterOff}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {untracked.length === 0 ? null : (
        <ul
          className="search__list search__list--operators"
          aria-label={strings.search.otherNetworks}
        >
          {untracked.map((operator) => {
            const active = filters.operatorNames.includes(operator.key)
            return (
              <li key={operator.key}>
                <button
                  className="search__result search__result--operator"
                  type="button"
                  aria-pressed={active}
                  onClick={() => toggleUntracked(operator)}
                >
                  <span
                    className="search__result-swatch"
                    style={{ backgroundColor: OTHER_OPERATOR_COLOR }}
                    aria-hidden="true"
                  />
                  <span className="search__result-name">{operator.name}</span>
                  <span className="search__result-hint">
                    {active ? strings.search.operatorFilterOn : strings.search.operatorFilterOff}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {!searching ? null : (
        <div className="search__results">
          <p className="search__count" aria-live="polite">
            {results.length === 0
              ? strings.search.noResults
              : strings.search.resultsCount(results.length)}
          </p>

          {results.length === 0 ? null : (
            <ul className="search__list" aria-label={strings.search.results}>
              {results.map((entry) => (
                <li key={entry.id}>
                  <button
                    className="search__result"
                    type="button"
                    onClick={() => setSelectedSiteId(entry.id)}
                  >
                    <span className="search__result-name">{entry.name}</span>
                    <span className="search__result-city">{entry.city}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </search>
  )
}
